/**
 * The Settings tab's body.
 *
 * ISSUE-952 R6 moves the three destinations with no other home, Rules, Chat
 * and Notifications, under Settings. D6 says nothing reachable before the
 * change may be unreachable after it, and those three were reachable by every
 * member from the old pool home card stack.
 *
 * The two pull against each other, because the settings PANE refuses a plain
 * member outright: `resolvePoolSettingsState` answers
 * `{ phase: "noAccess", reason: "role" }` at
 * `apps/app/src/features/poolSettings/poolSettingsState.ts`, and
 * `PoolSettingsPane` paints that refusal. Mounting the pane unguarded would
 * replace a member's Rules, Chat and Notifications with a refusal screen.
 *
 * The card records the resolution: the Settings TAB is not the settings PANE.
 * The tab renders the three rows for every member, and only the
 * commissioner-only settings cards inside it carry the role refusal. So the
 * rows are unconditional and the pane is mounted for an owner or an admin
 * only.
 *
 * Rules are the one row with two mounts, and exactly one of them renders per
 * role. `PoolHomeRulesCard` is the member-readable mount, and it says so in
 * its own header; the Commissioner and Admin mount is the settings pane's
 * `contest-rules` slot, which draws `PoolSettingsRulesCard`. Rendering both
 * put the same rules on the screen twice for a commissioner, and the two
 * carry one `pool-rules-summary` each, so `getByTestId` resolved to two
 * elements. The role picks the mount; nobody gets both and nobody gets none.
 *
 * The pane is mounted with no `onGoBack`. This tab is inside the pool, under
 * the pool header, and that header's back arrow is the one way out of a
 * pool, the same rule the Members and Results tabs follow. A "Back to pool"
 * here cleared `?tab=` and dropped the fan on Members (ISSUE-1035).
 */
import type { ReactNode } from "react";

import { PoolHomeChatCard } from "../poolHome/PoolHomeChatCard";
import { PoolHomeNotificationsCard } from "../poolHome/PoolHomeNotificationsCard";
import { PoolHomeRulesCard } from "../poolHome/PoolHomeRulesCard";
import type { typePoolRole } from "../poolHome/poolHomeState";
import { PoolNotificationsPane } from "../poolNotifications/PoolNotificationsPane";
import { PoolSettingsPane } from "../poolSettings/PoolSettingsPane";
import { readPoolSettingsPanel } from "../poolSettings/poolSettingsRoute";
import { poolTypePanes } from "./poolTypePanes";

export type typePoolSettingsTabPaneProps = {
  poolId: string;
  contestId?: string | undefined;
  viewerPoolRole: typePoolRole;
  chatEnabled: boolean;
  /** The Settings tab's own sub-address, from `?panel=`. */
  activePanel: string | null;
  onOpenChat: (chatId: string) => void;
  /** Back out of the notifications panel, staying on this tab. */
  onClosePanel: () => void;
  /** ISSUE-1334. A non-Survivor type renders its own section at the top. */
  contestType?: string | null | undefined;
};

export const PoolSettingsTabPane = ({
  poolId,
  contestId,
  viewerPoolRole,
  chatEnabled,
  activePanel,
  onOpenChat,
  onClosePanel,
  contestType = null,
}: typePoolSettingsTabPaneProps): ReactNode => {
  const panes = poolTypePanes(contestType);
  const isCommissioner =
    viewerPoolRole === "owner" || viewerPoolRole === "admin";

  // The per-pool notifications screen opens over this tab rather than beside
  // it: it is one row's detail, not a fifth thing on the tab.
  if (activePanel === "notifications") {
    return (
      <div className="flex flex-col gap-3" data-testid="pool-settings-tab">
        <PoolNotificationsPane poolId={poolId} onGoBack={onClosePanel} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="pool-settings-tab">
      {panes !== null ? (
        <panes.SettingsSection
          poolId={poolId}
          viewerPoolRole={viewerPoolRole}
        />
      ) : null}
      {isCommissioner || panes !== null ? null : (
        <PoolHomeRulesCard poolId={poolId} contestId={contestId} />
      )}
      <PoolHomeChatCard
        poolId={poolId}
        chatEnabled={chatEnabled}
        onOpenChat={onOpenChat}
      />
      <PoolHomeNotificationsCard poolId={poolId} />
      {isCommissioner ? (
        <PoolSettingsPane
          poolId={poolId}
          contestId={contestId}
          activePanel={readPoolSettingsPanel({
            panel: activePanel ?? undefined,
          })}
        />
      ) : null}
    </div>
  );
};
